import { useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnapCarousel } from 'react-snap-carousel';
import { useTheme } from 'styled-components';

import { useMediaQuery } from 'styled-breakpoints/use-media-query';

const AUTO_ADVANCE_MS = 5000;
const RESUME_AFTER_MS = 8000;

// Mobile swipe row: pips follow the card in view, and while the row is on
// screen it advances every 5s. A touch pauses it for 8s. Off with reduced motion.
export const useCardCarousel = () => {
  const { scrollRef, activePageIndex, pages, goTo } = useSnapCarousel();
  const isMobile = useMediaQuery(useTheme()?.breakpoints.down('md'));
  const reduceMotion = useReducedMotion();
  const [row, setRow] = useState<HTMLElement | null>(null);
  const [isInView, setIsInView] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout>>();
  // goTo is a new function every render; a ref keeps re-renders from resetting the timer.
  const goToRef = useRef(goTo);

  goToRef.current = goTo;

  const ref = useCallback(
    (el: HTMLElement | null) => {
      scrollRef(el);
      setRow(el);
    },
    [scrollRef]
  );

  useEffect(() => {
    if (!row) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.4 }
    );

    observer.observe(row);

    return () => observer.disconnect();
  }, [row]);

  useEffect(() => () => clearTimeout(holdTimer.current), []);

  useEffect(() => {
    if (!isMobile || reduceMotion || !isInView || isHeld || pages.length < 2) {
      return undefined;
    }

    const timer = setTimeout(
      () => goToRef.current((activePageIndex + 1) % pages.length),
      AUTO_ADVANCE_MS
    );

    return () => clearTimeout(timer);
  }, [isMobile, reduceMotion, isInView, isHeld, activePageIndex, pages.length]);

  const hold = () => {
    setIsHeld(true);
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => setIsHeld(false), RESUME_AFTER_MS);
  };

  return {
    ref,
    hold,
    goTo,
    activePageIndex,
    count: pages.length,
  };
};
